// src/pages/Login.jsx
import React, { useState } from 'react';
import {
  CognitoUser,
  AuthenticationDetails,
  CognitoUserPool
} from 'amazon-cognito-identity-js';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../routes/AuthContext';
import cognitoConfig from './cognitoConfig.json';

const userPool = new CognitoUserPool({
  UserPoolId: cognitoConfig.UserPoolId,
  ClientId: cognitoConfig.ClientId
});

// hardcoded demo password sent to Cognito no matter what user types
const DEMO_PASSWORD = 'GloboTek2025!';

// User data for auto-fill dropdown
const USER_EMAILS = [
  'merikson1234@gmail.com',
  'Ted@tmosby.com',
  'Barney@gmail.com',
  'Lily@me.com',
  'RanjitSingh@me.com',
  'qgarvey@gmail.com',
  'zoey@me.com',
  'jwhitaker1234@gmail.com',
  'Sandy.Rivers@is.com',
  'daniel.flores@gmail.com',
];

const USER_NAMES = [
  'Marshall Eriksen',
  'Ted Mosby',
  'Barney Stinson',
  'Lily Aldrin',
  'Ranjit Singh',
  'Quinn Garvey',
  'Zoey Pierson',
  'Jerry Whitaker',
  'Sandy Rivers',
  'Daniel Flores',
];

const Login = () => {
  const navigate = useNavigate();
  const { setAuthUser } = useAuth();

  // username/password for initial auth
  const [username, setUsername] = useState('');
  // user can type in password field but it will be ignored
  const [password, setPassword] = useState('');
  // selected user index (for tracking, not used in select anymore)
  const [selectedUserIndex, setSelectedUserIndex] = useState('');

  // challenge state for NEW_PASSWORD_REQUIRED
  const [stage, setStage] = useState('login'); // 'login' | 'newPasswordRequired' | 'done'
  const [tempUser, setTempUser] = useState(null); // holds CognitoUser during challenge
  // user can type this too but it will be ignored
  const [newPermPassword, setNewPermPassword] = useState('');

  // ui state
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // central success handler so both normal login and post-challenge reuse it
  const handleAuthSuccess = (result) => {
    // result is CognitoUserSession
    const serialized = JSON.stringify({
      idToken: result.getIdToken().getJwtToken(),
      accessToken: result.getAccessToken().getJwtToken(),
      refreshToken: result.getRefreshToken().getToken(),
      payload: result.getIdToken().decodePayload()
    });

    localStorage.setItem('authUser', serialized);

    // update context so header sees payload.given_name and custom:User_ID
    setAuthUser({
      getIdToken: () => result.getIdToken(),
      getAccessToken: () => result.getAccessToken(),
      getRefreshToken: () => result.getRefreshToken(),
      idToken: result.getIdToken(), // convenience
      accessToken: result.getAccessToken(),
      refreshToken: result.getRefreshToken()
    });

    setStage('done');
    setIsSubmitting(false);

    navigate('/');
  };

  // first step: username submit
  const handleSubmitLogin = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    const authDetails = new AuthenticationDetails({
      Username: username,
      Password: DEMO_PASSWORD // ignore whatever was typed
    });

    const cognitoUser = new CognitoUser({
      Username: username,
      Pool: userPool
    });

    cognitoUser.authenticateUser(authDetails, {
      onSuccess: (sessionResult) => {
        // normal path. user not forced to change password
        handleAuthSuccess(sessionResult);
      },
      onFailure: (err) => {
        console.error('Login failed:', err);
        setError(err.message || 'Login failed');
        setIsSubmitting(false);
      },
      newPasswordRequired: (userAttributes /* , requiredAttributes */) => {
        // Cognito says the user must set a new password now
        try {
          delete userAttributes.email_verified;
          delete userAttributes.phone_number_verified;
        } catch {}

        setTempUser(cognitoUser);
        setStage('newPasswordRequired');
        setIsSubmitting(false);
      }
    });
  };

  // second step: force-change password
  const handleSubmitNewPassword = (e) => {
    e.preventDefault();
    if (!tempUser) {
      setError('Session not found. Please log in again.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    // always send DEMO_PASSWORD no matter what user typed
    tempUser.completeNewPasswordChallenge(
      DEMO_PASSWORD,
      {},
      {
        onSuccess: (sessionResult) => {
          handleAuthSuccess(sessionResult);
        },
        onFailure: (err) => {
          console.error('Password update failed:', err);
          setError(err.message || 'Password update failed');
          setIsSubmitting(false);
        }
      }
    );
  };

  // Handle user selection from button
  const handleUserSelect = (index) => {
    if (index >= 0 && index < USER_EMAILS.length) {
      setUsername(USER_EMAILS[index]);
      setSelectedUserIndex(index.toString());
    }
  };

  // render states
  const renderLoginForm = () => (
    <form className="contact-form" onSubmit={handleSubmitLogin}>
      <div className="row row-gutter-10">
        <div className="col-12 col-lg-6">
          <input
            type="text"
            className="input-text"
            placeholder="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={isSubmitting}
            required
          />
        </div>

        <div className="col-12 col-lg-6">
          <input
            type="password"
            className="input-text"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isSubmitting}
            required
          />
        </div>

        <div className="col-12">
          {error && (
            <p className="error-message" style={{ color: 'red' }}>
              {error}
            </p>
          )}
        </div>

        <div className="col-12">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Logging in...' : 'Login'}
          </button>
        </div>

        {/* User selection - subtle, at bottom */}
        <div className="col-12" style={{ marginTop: '20px' }}>
          <details style={{ fontSize: '14px', color: '#666' }}>
            <summary style={{ cursor: 'pointer', userSelect: 'none' }}>
              Select a user to auto-fill
            </summary>
            <ul style={{ listStyle: 'none', padding: 0, marginTop: 8 }}>
              {USER_NAMES.map((name, index) => (
                <li key={index} style={{ marginBottom: 6 }}>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => handleUserSelect(index)}
                    disabled={isSubmitting}
                  >
                    {name} — {USER_EMAILS[index]}
                  </button>
                </li>
              ))}
            </ul>
          </details>
        </div>
      </div>
    </form>
  );

  const renderForceChangeForm = () => (
    <form className="contact-form" onSubmit={handleSubmitNewPassword}>
      <div className="row row-gutter-10">
        <div className="col-12">
          <p>Your account requires a new password before you can continue.</p>
        </div>

        <div className="col-12 col-lg-6">
          <input
            type="password"
            className="input-text"
            placeholder="New Password"
            value={newPermPassword}
            onChange={(e) => setNewPermPassword(e.target.value)}
            disabled={isSubmitting}
            required
          />
        </div>

        <div className="col-12">
          {error && (
            <p className="error-message" style={{ color: 'red' }}>
              {error}
            </p>
          )}
        </div>

        <div className="col-12">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Updating...' : 'Set New Password'}
          </button>
        </div>
      </div>
    </form>
  );

  const renderCompleted = () => (
    <div className="success-message-container">
      <h2>Login Successful</h2>
      <p>You are now logged in.</p>
    </div>
  );

  return (
    <section className="contact-section">
      <div className="container">
        <div className="row">
          {/* left side info */}
          <div className="col-lg-4">
            <div className="contact-box">
              <div className="section-tagline">USER LOGIN</div>
              <h1 className="section-title">Access Your Account</h1>
              <p>
                Login to access personalized services and manage your
                interactions with GTS Nation. If you don’t have an account,
                please contact support.
              </p>
            </div>
          </div>

          {/* right side form */}
          <div className="col-lg-8">
            {stage === 'login' && renderLoginForm()}
            {stage === 'newPasswordRequired' && renderForceChangeForm()}
            {stage === 'done' && renderCompleted()}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Login;
